import {AbstractViewModel} from '../../../core/interfaces/abstract-view-model';
import {ProjectApiModel} from '../../../api/models/project.api-model';
import {VehicleViewModel} from './vehicle.view-model';
import {UserViewModel} from '../settings/user.view-model';
import {CurrentUserViewModel} from '../../../core/models/current-user.view-model';
import {MediaObjectViewModel} from '../../../core/models/media-object.view-model';
import {MediaObjectApiModel} from '../../../api/models/media-object.api-model';
import {HashtagApiModel} from '../../../api/models/hashtag.api.model';
import {RatingApiModel} from '../../../api/models/rating.api-model';
import {FollowApiModel} from '../../../api/models/follow.api-model';
import {GarageStatusEnum} from './garage-status.enum';
import {ReactionApiModel} from '../../../api/models/reaction.api.model';
import {VehicleAttributes} from "../../../core/interfaces/vehicle-attributes";
import {EquipmentApiModel} from "../../../api/models/equipment.api-model";
import {UserApiModel} from "../../../api/models/user.api-model";
import {OwnerApiModel} from "../../../api/interfaces/owner-api.model";
import {VehicleTypeEnum} from "../../../core/enums/vehicle-type.enum";
import {ProjectVehicleParametersOverrideApiModel} from "../../../api/models/project-vehicle-parameters-override.api-model";
import {VehicleParameterViewModel} from "../project/parameters/vehicle-parameter-view-model.interface";
import {VehicleRegistrationData} from './vehicle-registration-data';

export class ProjectViewModel extends AbstractViewModel<ProjectApiModel> {

  public constructor(protected apiModel: ProjectApiModel = new ProjectApiModel()) {
    super(apiModel);
    this.fromApiModel(apiModel);
  }
  public user: UserViewModel|CurrentUserViewModel;
  public vehicle: VehicleViewModel;
  public status: GarageStatusEnum;
  public isOnSale: boolean;
  public vehicleType: VehicleTypeEnum;
  public shortDesc: string = null;
  public alias: string = null;
  public identity: string = null;
  public maskedVin: string | null = null;
  // public registrationPlate: string = null;
  // public vin: string = null;
  // public firstRegistrationInCountry: Date = null;
  public externalHistoryAllowedAt: Date = null;
  public slug: string = null;
  public hashtags: HashtagApiModel[] = [];
  public owners: OwnerApiModel[] = [];
  public ratings: RatingApiModel[] = [];
  public averageRating: number;
  public ratingCount: number;
  public currentUserRating: number;
  public theme: MediaObjectViewModel = null;
  public thumbnail: string = null;
  public locked: boolean = null;
  public media: string[] = null;
  public instagramProfileUrl: string | null = null;
  public instagramUserProfileUrl: string | null = null;
  public instagramSyncSources: string[] = ['user'];
  public instagramLinkedUsername: string | null = null;
  public instagramIntegrationStatus: string | null = null;
  public instagramSyncLimitations: string[] = [];
  public instagramProfileMismatch = false;
  public documents: string[] = null;
  public canReadDocuments = false;
  public follows: FollowApiModel[] = [];
  public reactions: ReactionApiModel[] = [];
  public likes: string[] = [];
  public updatedAt: Date;
  public createdAt: Date;
  public isPlaceholder: boolean = false;
  public attributes: VehicleAttributes;
  public equipment: EquipmentApiModel[] = [];
  public vehicleParametersOverride: ProjectVehicleParametersOverrideApiModel | null = null;
  public vehicleParameterViewModels: VehicleParameterViewModel[] = [];
  public isPendingCoOwner: boolean = false;
  public invitationToken: string | null = null;

  public static create(currentUserVM: CurrentUserViewModel, vehicleVM: VehicleViewModel, registration?: VehicleRegistrationData): ProjectViewModel {
    const projectVM = new ProjectViewModel();
    projectVM.user = currentUserVM;
    projectVM.vehicle = vehicleVM;
    if (registration) {
      projectVM.attributes = registration as any;
    }
    return projectVM;
  }

  fromApiModel(apiModel: ProjectApiModel): this {
    this.id = apiModel.id;
    this.shortDesc = apiModel.shortDesc;
    this.alias = apiModel.alias;
    this.identity = (apiModel as any)?.identity || null;
    this.maskedVin = apiModel.maskedVin || null;
    this.slug = apiModel.slug;
    this.externalHistoryAllowedAt = apiModel.externalHistoryAllowedAt;
    this.status = apiModel.status;
    this.isOnSale = !!apiModel.isOnSale;
    this.vehicleType = apiModel.vehicleType;
    this.ratings = apiModel.ratings;
    this.averageRating = apiModel.averageRating;
    this.ratingCount = apiModel.ratingCount;
    this.currentUserRating = apiModel.currentUserRating;
    if (typeof apiModel.theme === 'string') {
      const themeVM = new MediaObjectViewModel();
      themeVM['@id'] = apiModel.theme;
      this.theme = themeVM;
    } else if (apiModel.theme) {
      this.theme = new MediaObjectViewModel(apiModel.theme as MediaObjectApiModel);
    } else {
      this.theme = null;
    }
    if (typeof apiModel.user === 'string') {
      const slug = apiModel.user.split('/').pop();
      const userVM = new UserViewModel();
      userVM['@id'] = apiModel.user;
      userVM.slug = slug || null;
      this.user = userVM;
    } else {
      this.user = new UserViewModel(apiModel.user as UserApiModel);
    }
    if (typeof apiModel.vehicle === 'string') {
      const vehicleVM = new VehicleViewModel();
      vehicleVM['@id'] = apiModel.vehicle;
      const vehicleId = Number((apiModel.vehicle || '').split('/').pop());
      if (Number.isFinite(vehicleId)) {
        vehicleVM.id = vehicleId;
      }
      this.vehicle = vehicleVM;
    } else if (apiModel.vehicle) {
      this.vehicle = new VehicleViewModel(apiModel.vehicle as any);
    } else {
      this.vehicle = null;
    }
    this.locked = apiModel.locked;
    this.updatedAt = apiModel.updatedAt;
    this.createdAt = apiModel.createdAt;
    this.follows = apiModel.follows || [];
    this.reactions = apiModel.reactions || [];
    this.media = apiModel.media || [];
    this.instagramProfileUrl = apiModel.instagramProfileUrl || null;
    this.instagramUserProfileUrl = apiModel.instagramUserProfileUrl || null;
    this.instagramSyncSources = this.normalizeInstagramSyncSources(apiModel.instagramSyncSources);
    this.instagramLinkedUsername = apiModel.instagramLinkedUsername || null;
    this.instagramIntegrationStatus = apiModel.instagramIntegrationStatus || null;
    this.instagramSyncLimitations = apiModel.instagramSyncLimitations || [];
    this.instagramProfileMismatch = !!apiModel.instagramProfileMismatch;
    this.documents = apiModel.documents || [];
    this.canReadDocuments = apiModel.canReadDocuments === true;
    this.equipment = apiModel.equipment as EquipmentApiModel[] || [];
    this.vehicleParametersOverride = apiModel.vehicleParametersOverride as ProjectVehicleParametersOverrideApiModel || null;
    this.vehicleParameterViewModels = apiModel.vehicleParameterViewModels || [];
    this.owners = apiModel.owners as OwnerApiModel[] || [];
    if (apiModel.attributes) {
      this.attributes = apiModel.attributes;
      this.attributes.firstRegistrationInCountry = this.parseDateOnly(this.attributes.firstRegistrationInCountry as any);
    }
    return this;
  }

  getApiModel(): ProjectApiModel {
    return this.apiModel;
  }

  toApiModel(): ProjectApiModel {
    const hashtagStrings  = this.hashtags.map((e: HashtagApiModel) => {
      return e['@id'] ? e['@id'] : e.name;
    });
    this.apiModel.user = this.user['@id'];
    this.apiModel.vehicle = this.vehicle['@id'];
    this.apiModel.theme = this.theme?.['@id'] || null;
    this.apiModel.shortDesc = this.shortDesc;
    this.apiModel.alias = this.alias;
    this.apiModel.instagramProfileUrl = this.instagramProfileUrl;
    this.apiModel.instagramSyncSources = this.normalizeInstagramSyncSources(this.instagramSyncSources);
    this.apiModel.hashtags = hashtagStrings;
    this.apiModel.owners = this.owners;
    if (this.attributes) {
      this.apiModel.attributes = {
        ...this.attributes,
        firstRegistrationInCountry: this.formatDateOnly(this.attributes.firstRegistrationInCountry as any)
      } as any;
    }
    this.apiModel.vehicleParametersOverride = this.vehicleParametersOverride as any;
    this.apiModel.vehicleParameterViewModels = this.vehicleParameterViewModels;
    return this.apiModel;
  }

  public get displayVehicleIdentity(): string | null {
    return this.vehicle?.fullVehicleIdentity || this.identity || this.alias || this.slug || null;
  }

  private parseDateOnly(value: string | Date | null | undefined): Date | null {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      return value;
    }
    const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      const year = Number(match[1]);
      const month = Number(match[2]) - 1;
      const day = Number(match[3]);
      return new Date(year, month, day);
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private formatDateOnly(value: string | Date | null | undefined): string | null {
    if (!value) {
      return null;
    }
    if (typeof value === 'string') {
      return value;
    }
    if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
      return null;
    }
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private normalizeInstagramSyncSources(value: any): string[] {
    const allowed = new Set(['user', 'project']);
    const rawItems = Array.isArray(value) ? value : (value ? [value] : []);
    const normalized: string[] = [];

    for (const item of rawItems) {
      if (typeof item === 'string') {
        const source = item.trim().toLowerCase();
        if (allowed.has(source)) {
          normalized.push(source);
        }
        continue;
      }

      if (item && typeof item === 'object') {
        const flattened = Object.values(item)
          .map((entry: any) => typeof entry === 'string' ? entry.trim().toLowerCase() : '')
          .filter((entry: string) => entry.length > 0);

        for (const entry of flattened) {
          if (allowed.has(entry)) {
            normalized.push(entry);
          } else if (entry === 'u') {
            normalized.push('user');
          } else if (entry === 'p') {
            normalized.push('project');
          }
        }
      }
    }

    const unique = Array.from(new Set(normalized));
    return unique.length ? unique : ['user'];
  }
}
